import { browser } from '$app/environment';
import { notInCatalogGames } from './providers.js';
import { CatalogFilter, cfgCdnBase, cfgCdnTiles, cfgNewGamesIncludeLive, cfgToThemeCdnPath, FeatureSet, getGameSetLimit, getRealm, ReleaseStage, stateCatalogFilter, 
// stateCatalogLanguage,
stateCountry, stateFeatureSet, stateRealm, stateTheme, stateUi, Theme, wantAtLeastReleaseStage } from '@infinitro/config';
import { derived, get, writable } from 'svelte/store';
import { sports } from './sports';
import { sportsFomo } from './sportsFomo';
import { sportsRocketbet } from './sportsRocketbet';
import { sportsTurboslots } from './sportsTurboSlots';
export var GameSort;
(function (GameSort) {
    GameSort["Alphabetical"] = "alphabetical";
    GameSort["Newest"] = "new_releases";
    GameSort["Popular"] = "popular";
    GameSort["Popular25"] = "popular_25";
    GameSort["AlphabeticalZA"] = "alphabetical_za";
    GameSort["Popular50"] = "popular_50";
})(GameSort || (GameSort = {}));
export var GameCategory;
(function (GameCategory) {
    GameCategory["All"] = "all";
    GameCategory["Favorites"] = "favorites";
    GameCategory["FeatureBuyIn"] = "featurebuyin";
    GameCategory["GameShows"] = "gameshows";
    GameCategory["HotLive"] = "hotlive";
    GameCategory["HotSlots"] = "hotslots";
    GameCategory["LiveCasino"] = "livecasino";
    GameCategory["NewGames"] = "newgames";
    GameCategory["Originals"] = "originals";
    GameCategory["Popular"] = "populargames";
    GameCategory["Providers"] = "providers";
    GameCategory["RecentlyPlayed"] = "recentlyplayed";
    GameCategory["Slots"] = "slots";
    GameCategory["Sports"] = "sports";
    GameCategory["Tables"] = "tables";
})(GameCategory || (GameCategory = {}));
const theme = get(stateTheme);
const GEO_GROUP1_RESTRICTED = ['US', 'GB', 'SE', 'RO', 'GR', 'DK', 'CZ', 'NL', 'CH', 'BG', 'CA'];
const GEO_COUNTRY_WAIT_TIMEOUT = 5000;
const GEO_COUNTRY_POLL_INTERVAL = 100;
const getGeoGroupForCountry = (country) => GEO_GROUP1_RESTRICTED.includes(country.toUpperCase()) ? 1 : 2;
let _country = null;
let _geoGroup = 1;
let _loadedGeoGroup = null;
const initialCountry = get(stateCountry);
if (initialCountry) {
    _country = initialCountry;
    _geoGroup = getGeoGroupForCountry(initialCountry);
}
const sportsItemsMap = {
    [Theme.Fomo]: sportsFomo,
    [Theme.Luna]: sportsFomo,
    [Theme.Rocketbet]: sportsRocketbet,
    [Theme.Turboslots]: sportsTurboslots
};
export const CDN_BASE = cfgCdnBase();
export const CDN_TILES = cfgCdnTiles();
// THIS FUNCTION CAN NEVER BE MODIFIED
export const toGameSlug = (game) => {
    // matches url_alphanumeric_underscore in backend
    let slug = game.toLowerCase().trim().replace(/ /g, '_');
    slug = slug.replace('&', 'and'); // swap ampersand to and
    slug = slug.replace("'s", 's'); // remove possessive 's
    slug = slug.replace(/[^a-z0-9_]/g, ''); // remove non-alphanumeric+underscore
    slug = slug.replace('__', '_').replace('__', '_').replace('__', '_').replace('__', '_');
    return slug;
};
// THIS FUNCTION CAN NEVER BE MODIFIED
export const toProviderSlug = (prov) => toGameSlug(prov.replace(/-/g, '_')).replace(/_/g, '-');
/**
 * Normalizes game titles for search: case-insensitive, strips punctuation and
 * whitespace so queries like "Dragons Crash" match "Dragon's Crash".
 */
const normalizeForGameSearch = (s) => s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]/gu, '');
export const toGameTile = (game, prov, locale) => {
    if (locale && ['ja', 'ko'].includes(locale)) {
        return `${CDN_TILES}/t/${toProviderSlug(prov)}/${locale}/${toGameSlug(game)}`;
    }
    return `${CDN_TILES}/t/${toProviderSlug(prov)}/${toGameSlug(game)}`;
};
export const expandGameInfo = (item) => {
    if (item.game && !item.game_slug)
        item.game_slug = toGameSlug(item.game);
    if (item.p && !item.prov)
        item.prov = providers.find((prov) => prov.p === item.p)?.name;
    if (item.prov && !item.provider_slug)
        item.provider_slug = toProviderSlug(item.prov);
    if (!item.alt && (item.name || item.game)) {
        item.alt = item.name || item.game;
        if (item.prov)
            item.alt += ` by ${item.prov}`;
    }
    if (!item.tile) {
        const stagePath = get(stateFeatureSet) === FeatureSet.Preview ? '/staging' : '';
        if (!item.tile && item.game && item.prov) {
            const isAnimatedGame = item.f !== undefined ? (item.f & (1 << 16)) !== 0 : false;
            const animatedPath = isAnimatedGame ? 'st/' : '';
            item.tile = `${CDN_TILES}${stagePath}/t/${item.provider_slug}/${animatedPath}${item.game_slug}`;
        }
    }
    return item;
};
export const expandGameInfos = (games) => games.map(expandGameInfo);
export const DEFAULT_PAGE_SIZE = 24;
//falls back to 300 if not set in theme settings
const THEME_GAMESET_LIMIT = getGameSetLimit();
const RETRY_INTERVAL = 3000; // 3 seconds
const TIMEOUT_INTERVAL = 10000; // 10 seconds
const REFRESH_INTERVAL = 60 * 1000 * 30; // 30 minutes
let initializing = undefined;
let initialized = false;
let catalog = []; // todo: should be write the catalog or a catalog state to the store?
let providers = [];
export const stateCatalogVersion = writable(0);
export const stateGameCategoryCounts = writable({
    [GameCategory.All]: 0,
    [GameCategory.Favorites]: 0,
    [GameCategory.FeatureBuyIn]: 0,
    [GameCategory.GameShows]: 0,
    [GameCategory.HotLive]: 0,
    [GameCategory.HotSlots]: 0,
    [GameCategory.LiveCasino]: 0,
    [GameCategory.NewGames]: 0,
    [GameCategory.Originals]: 0,
    [GameCategory.Popular]: 0,
    [GameCategory.Providers]: 0,
    [GameCategory.RecentlyPlayed]: 0,
    [GameCategory.Slots]: 0,
    [GameCategory.Sports]: 0,
    [GameCategory.Tables]: 0
});
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
export const getProviders = async () => {
    await initializeGameCatalog();
    return providers;
};
export const isSoftgaming = (f) => (f & (1 << 0)) !== 0;
export const isGametech = (f) => (f & (1 << 1)) !== 0;
export const hasSgDemo = (f) => (f & (1 << 3)) !== 0;
export const hasGtDemo = (f) => (f & (1 << 4)) !== 0;
export const isGameshow = (f) => (f & (1 << 5)) !== 0;
export const isLive = (f) => (f & (1 << 6)) !== 0;
export const isTablegame = (f) => (f & (1 << 7)) !== 0;
export const hasBonusBuy = (f) => (f & (1 << 8)) !== 0;
export const hasFreespins = (f) => (f & (1 << 9)) !== 0;
export const hasMegaways = (f) => (f & (1 << 10)) !== 0;
export const hasFreerounds = (f) => (f & (1 << 11)) !== 0;
export const hasFreebonus = (f) => (f & (1 << 12)) !== 0;
export const isBonusExcluded = (f) => (f & (1 << 13)) !== 0;
export const isRaceExcluded = (f) => (f & (1 << 14)) !== 0;
export const isHot = (f) => (f & (1 << 15)) !== 0;
export const isOriginal = (f) => (f & (1 << 17)) !== 0;
export const getGame = async (provider_slug, game_slug) => {
    await initializeGameCatalog();
    const findFunc = (game) => game.provider_slug === provider_slug && game.game_slug === game_slug;
    const found = catalog.find(findFunc);
    if (found || !get(stateUi).layout.showNotInCatalogGames)
        return found;
    return notInCatalogGames.map((a) => expandGameInfo(a)).find(findFunc);
};
export const getCategoryGames = async (category, provider) => {
    await initializeGameCatalog();
    return getCategoryGamesSync(category, provider);
};
const getCategoryGamesSync = (category, provider) => {
    // this function is not async and requires prior initialization of the catalog
    let filteredGames = catalog;
    const sportItems = sportsItemsMap[theme] ?? sports;
    if (!category)
        category = GameCategory.All;
    if (provider?.toLowerCase() === 'all')
        provider = undefined;
    // first filter by category
    switch (category) {
        case GameCategory.All:
            break;
        case GameCategory.FeatureBuyIn:
            filteredGames = filteredGames.filter((game) => hasBonusBuy(game.f ?? 0) && !isOriginal(game.f ?? 0));
            break;
        case GameCategory.GameShows:
            filteredGames = filteredGames.filter((game) => isGameshow(game.f ?? 0) && !isOriginal(game.f ?? 0));
            break;
        case GameCategory.LiveCasino:
            filteredGames = filteredGames.filter((game) => isLive(game.f ?? 0) && !isOriginal(game.f ?? 0));
            break;
        case GameCategory.HotLive:
            filteredGames = filteredGames.filter((game) => isHot(game.f ?? 0) && isLive(game.f ?? 0) && !isOriginal(game.f ?? 0));
            break;
        case GameCategory.HotSlots:
            filteredGames = filteredGames.filter((game) => isHot(game.f ?? 0) &&
                !isLive(game.f ?? 0) &&
                !isTablegame(game.f ?? 0) &&
                !isOriginal(game.f ?? 0));
            break;
        case GameCategory.NewGames:
            // only games with a real created date (or rank) are actually new
            filteredGames = filteredGames
                .filter((game) => (cfgNewGamesIncludeLive() || !isLive(game.f ?? 0)) &&
                !isOriginal(game.f ?? 0) &&
                ((game.c || game.created || 0) > 0 || game.r != null))
                .sort((a, b) => (b.c || b.created || 0) - (a.c || a.created || 0))
                .slice(0, THEME_GAMESET_LIMIT);
            break;
        case GameCategory.Originals:
            provider = undefined;
            if ([Theme.Fomo, Theme.Luna].includes(theme)) {
                filteredGames = filteredGames.filter((game) => game.prov?.toLowerCase().includes('originals') || isOriginal(game.f ?? 0));
            }
            else if (wantAtLeastReleaseStage(ReleaseStage.Alpha)) {
                filteredGames = [
                    ...originals.filter((game) => !game.not_available),
                    ...filteredGames.filter((game) => game.prov?.toLowerCase().includes('originals') || isOriginal(game.f ?? 0))
                ];
            }
            else {
                filteredGames = filteredGames.filter((game) => game.prov?.toLowerCase().includes('originals') || isOriginal(game.f ?? 0));
            }
            break;
        case GameCategory.Providers:
            // todo: add rank (popularity) and created (newest) for provider data
            provider = undefined;
            filteredGames = providers.map((prov) => ({ game: prov.name, prov: prov.name }));
            break;
        case GameCategory.Popular:
            // only games with a real rank (fake_rank from banker coalesces into r)
            filteredGames = filteredGames
                .filter((game) => !isLive(game.f ?? 0) &&
                !isOriginal(game.f ?? 0) &&
                !isGameshow(game.f ?? 0) &&
                game.r != null)
                .sort((a, b) => (a.r || 1000000) - (b.r || 1000000))
                .slice(0, THEME_GAMESET_LIMIT);
            break;
        case GameCategory.Sports:
            provider = undefined;
            filteredGames = sportItems.filter((game) => !game.not_available);
            break;
        case GameCategory.Slots:
            // only slot games
            filteredGames = filteredGames.filter((game) => !isLive(game.f ?? 0) &&
                !isTablegame(game.f ?? 0) &&
                !isOriginal(game.f ?? 0) &&
                !isGameshow(game.f ?? 0));
            break;
        case GameCategory.Tables:
            // not presently shown
            filteredGames = filteredGames.filter((game) => isTablegame(game.f ?? 0) && !isOriginal(game.f ?? 0));
            break;
        case GameCategory.RecentlyPlayed:
            // todo: implement via API query or local storage
            filteredGames = [];
            break;
        case GameCategory.Favorites:
            filteredGames = [];
            break;
    }
    return filteredGames;
};
export const categoryHasGames = async (category, provider) => {
    // non-optimized version:
    // 		const filteredGames = await getCategoryGames(category, provider);
    // 		return filteredGames.length > 0;
    // optimized version:
    switch (category) {
        case GameCategory.All:
        case GameCategory.Providers:
        case GameCategory.Popular:
        case GameCategory.NewGames:
        case GameCategory.Slots:
            return true;
        case GameCategory.RecentlyPlayed:
        case GameCategory.Favorites:
            return false;
    }
    await initializeGameCatalog();
    const sportItems = sportsItemsMap[theme] ?? sports;
    if (!category)
        category = GameCategory.All;
    if (provider?.toLowerCase() === 'all')
        provider = undefined;
    switch (category) {
        case GameCategory.FeatureBuyIn:
            return catalog.find((game) => hasBonusBuy(game.f ?? 0)) !== undefined;
        case GameCategory.GameShows:
            return catalog.find((game) => isGameshow(game.f ?? 0)) !== undefined;
        case GameCategory.LiveCasino:
            return catalog.find((game) => isLive(game.f ?? 0)) !== undefined;
        case GameCategory.HotLive:
            return (catalog.find((game) => isHot(game.f ?? 0) && isLive(game.f ?? 0)) !== undefined);
        case GameCategory.HotSlots:
            return (catalog.find((game) => isHot(game.f ?? 0) && !isLive(game.f ?? 0) && !isTablegame(game.f ?? 0)) !== undefined);
        case GameCategory.Originals:
            if ([Theme.Fomo, Theme.Luna].includes(theme)) {
                return (catalog.find((game) => game.prov?.toLowerCase().includes('originals')) !==
                    undefined);
            }
            else {
                return catalog.find((game) => !game.not_available) !== undefined;
            }
        case GameCategory.Sports:
            return sportItems.find((game) => !game.not_available) !== undefined;
        case GameCategory.Tables:
            return catalog.find((game) => isTablegame(game.f ?? 0)) !== undefined;
    }
    return false;
};
export const getCategoryGameCount = async (provider, category) => {
    const filteredGames = await getCategoryGames(category, provider);
    return filteredGames.length;
};
export const getGames = async (page = 1, pageSize = DEFAULT_PAGE_SIZE, provider, category, sort, search) => {
    await initializeGameCatalog();
    let filteredGames = catalog;
    if (!category)
        category = GameCategory.All;
    if (!sort)
        sort = GameSort.Popular;
    if (provider?.toLowerCase() === 'all')
        provider = undefined;
    // first filter by category
    filteredGames = getCategoryGamesSync(category, provider);
    // Second, filter providers to only those that exist in the category
    let filteredProviders;
    switch (category) {
        case GameCategory.Originals:
            if (![Theme.Fomo, Theme.Luna, Theme.Turboslots].includes(theme)) {
                filteredProviders = [originalProvider];
            }
            else {
                filteredProviders = providers.filter((prov) => filteredGames.find((game) => game.prov === prov.name));
            }
            break;
        case GameCategory.Sports:
            filteredProviders = [sportsProvider];
            break;
        default:
            filteredProviders = providers.filter((prov) => filteredGames.find((game) => game.prov === prov.name));
            break;
    }
    // Third, filter games by provider, search, sort and paginate
    const gameCount = filteredGames.length;
    const normalizedSearch = search?.trim() ? normalizeForGameSearch(search.trim()) : '';
    filteredGames = filteredGames
        .filter((game) => !provider || game.prov === provider)
        .filter((game) => !normalizedSearch ||
        normalizeForGameSearch(game.game).includes(normalizedSearch) ||
        (game.ja && normalizeForGameSearch(game.ja).includes(normalizedSearch)) ||
        (game.ko && normalizeForGameSearch(game.ko).includes(normalizedSearch)))
        .filter((game) => {
        if (sort === GameSort.Popular25) {
            const rank = game.r || 1000000;
            return rank > 24;
        }
        else if (sort === GameSort.Popular50) {
            const rank = game.r || 1000000;
            return rank > 49;
        }
        return true;
    })
        .sort((a, b) => {
        switch (sort) {
            case GameSort.Alphabetical:
                return a.game.localeCompare(b.game);
            case GameSort.AlphabeticalZA:
                return b.game.localeCompare(a.game);
            case GameSort.Newest:
                return (b.c || b.created || 0) - (a.c || a.created || 0);
            case GameSort.Popular25:
            case GameSort.Popular50:
            default: // GameSort.Popular:
                return (a.r || 1000000) - (b.r || 1000000);
        }
    });
    filteredGames = filteredGames.slice((page - 1) * pageSize, page * pageSize);
    return [filteredGames, filteredProviders, gameCount > page * pageSize];
};
let _catalogFilter = CatalogFilter.ReleaseGames;
stateCatalogFilter.subscribe((value) => {
    _catalogFilter = value;
});
const getGamesRelease = () => {
    const url = (() => {
        switch (_catalogFilter) {
            case CatalogFilter.AllGames:
            case CatalogFilter.AllProviders:
                return `preview`;
            default:
                return `release`;
        }
    })();
    return url;
};
const getProvidersRelease = () => {
    const url = (() => {
        switch (_catalogFilter) {
            case CatalogFilter.AllGames:
            case CatalogFilter.AllProviders:
                return `preview`;
            default:
                return `release`;
        }
    })();
    return url;
};
export const getGamesJsonUrl = (geoGroup) => {
    // langauge is presently unused, and may get removed as they want search in JA/KO to work from any language
    const url = (() => {
        return `${cfgCdnBase()}/catalog/${realm}/games_${getGamesRelease()}_providers_${getProvidersRelease()}_en_geo_${geoGroup}.json`;
    })();
    console.log('[catalog-min] getGamesJsonUrl', url);
    return url;
};
export const getProvidersJsonUrl = (geoGroup) => {
    // langauge is presently unused, and may get removed as they want search in JA/KO to work from any language
    const url = (() => {
        return `${cfgCdnBase()}/catalog/${realm}/providers_${getProvidersRelease()}_games_${getGamesRelease()}_en_geo_${geoGroup}.json`;
    })();
    console.log('[catalog-min] getProvidersJsonUrl', url);
    return url;
};
const waitForCountryIfNeeded = async () => {
    if (!browser || get(stateCountry))
        return;
    const deadline = Date.now() + GEO_COUNTRY_WAIT_TIMEOUT;
    while (!get(stateCountry) && Date.now() < deadline) {
        await sleep(GEO_COUNTRY_POLL_INTERVAL);
    }
    const country = get(stateCountry);
    if (country && _country !== country) {
        _country = country;
        _geoGroup = getGeoGroupForCountry(country);
    }
};
const catalogNeedsFetch = (refresh, refreshGroup) => !initialized || !!refresh || !!refreshGroup || _geoGroup !== _loadedGeoGroup;
export const initializeGameCatalog = async (refresh, refreshGroup) => {
    if (refresh) {
        _loadedGeoGroup = null;
    }
    while (catalogNeedsFetch(refresh, refreshGroup)) {
        try {
            if (initializing) {
                while (initializing && initializing.getTime() > new Date().getTime() - TIMEOUT_INTERVAL) {
                    await sleep(200);
                }
                if (!catalogNeedsFetch(refresh, refreshGroup)) {
                    return [catalog, providers];
                }
            }
            if (!get(stateCountry)) {
                await waitForCountryIfNeeded();
            }
            initializing = new Date();
            while (_geoGroup !== _loadedGeoGroup) {
                const _loadingGeoGroup = _geoGroup;
                const url1 = getProvidersJsonUrl(_loadingGeoGroup);
                const url2 = getGamesJsonUrl(_loadingGeoGroup);
                let response = await fetch(url1, { cache: 'no-store' });
                if (_loadingGeoGroup !== _geoGroup)
                    continue;
                if (!response.ok)
                    throw new Error(`Failed to fetch game catalog: ${response.statusText}`);
                providers = await response.json();
                providers = providers.filter((prov) => prov?.count && prov?.count > 0);
                response = await fetch(url2, { cache: 'no-store' });
                if (_loadingGeoGroup !== _geoGroup)
                    continue;
                if (!response.ok)
                    throw new Error(`Failed to fetch game catalog: ${response.statusText}`);
                catalog = await response.json();
                catalog = catalog.map(expandGameInfo);
                _loadedGeoGroup = _loadingGeoGroup;
            }
            initialized = catalog.length > 0;
            if (initialized) {
                if (initializing)
                    console.log(`loaded ${catalog.length} games in ${new Date().getTime() - initializing.getTime()} milliseconds`);
                initializing = undefined;
                updateCategoryCounts();
                stateCatalogVersion.update((v) => v + 1);
                return [catalog, providers];
            }
        }
        catch (error) {
            initializing = undefined;
            console.log(error);
        }
        await sleep(RETRY_INTERVAL);
    }
    return [catalog, providers];
};
const updateCategoryCounts = () => {
    const counts = {
        [GameCategory.All]: getCategoryGamesSync(GameCategory.All).length,
        [GameCategory.Favorites]: getCategoryGamesSync(GameCategory.Favorites).length,
        [GameCategory.FeatureBuyIn]: getCategoryGamesSync(GameCategory.FeatureBuyIn).length,
        [GameCategory.GameShows]: getCategoryGamesSync(GameCategory.GameShows).length,
        [GameCategory.HotLive]: getCategoryGamesSync(GameCategory.HotLive).length,
        [GameCategory.HotSlots]: getCategoryGamesSync(GameCategory.HotSlots).length,
        [GameCategory.LiveCasino]: getCategoryGamesSync(GameCategory.LiveCasino).length,
        [GameCategory.NewGames]: getCategoryGamesSync(GameCategory.NewGames).length,
        [GameCategory.Originals]: getCategoryGamesSync(GameCategory.Originals).length,
        [GameCategory.Popular]: getCategoryGamesSync(GameCategory.Popular).length,
        [GameCategory.Providers]: getCategoryGamesSync(GameCategory.Providers).length,
        [GameCategory.RecentlyPlayed]: getCategoryGamesSync(GameCategory.RecentlyPlayed).length,
        [GameCategory.Slots]: getCategoryGamesSync(GameCategory.Slots).length,
        [GameCategory.Sports]: getCategoryGamesSync(GameCategory.Sports).length,
        [GameCategory.Tables]: getCategoryGamesSync(GameCategory.Tables).length
    };
    console.log(counts);
    stateGameCategoryCounts.set(counts);
};
setInterval(() => initializeGameCatalog(true), REFRESH_INTERVAL);
// Generate a unique game ID in the format: provider_slug#game_slug
export const getGameId = (game) => {
    return `${game.provider_slug}#${game.game_slug}`;
};
export const originals = [Theme.Flush].includes(theme) && wantAtLeastReleaseStage(ReleaseStage.Alpha)
    ? [
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/dice.png`),
            game: 'Dice',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/mines.png`),
            game: 'Mines',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/wheel.png`),
            game: 'Wheel',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/hilo.png`),
            game: 'HiLo',
            prov: 'Originals',
            not_available: true
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/plinko.png`),
            game: 'Plinko',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/blackjack.png`),
            game: 'Blackjack',
            prov: 'Originals',
            not_available: true
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/baccarat.png`),
            game: 'Baccarat',
            prov: 'Originals',
            not_available: true
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/roulette.png`),
            game: 'Roulette',
            prov: 'Originals',
            not_available: true
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/russian-roulette.png`),
            game: 'Russian Roulette',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/satoshi-wheel.png`),
            game: 'Satoshi Wheel',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/crash.png`),
            game: 'Crash',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/ape-crash.png`),
            game: 'Ape Crash',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/video-poker.png`),
            game: 'Video Poker',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/keno.png`),
            game: 'Keno',
            prov: 'Originals'
        },
        {
            tile: cfgToThemeCdnPath(`/originals/tiles/roulette.png`),
            game: 'Roulette',
            prov: 'Originals'
        }
    ]
    : [];
export const comingSoonItems = [
    {
        tile: `${CDN_BASE}/images/misc/original-soon-1.png`,
        game: '#',
        prov: 'Flush Originals'
    },
    {
        tile: `${CDN_BASE}/images/misc/original-soon-2.png`,
        game: '#',
        prov: 'Flush Originals'
    }
];
const originalProvider = {
    name: 'Flush Originals',
    prov: 'Flush Originals',
    count: originals.length
};
const sportsProvider = {
    name: 'Sports',
    prov: 'Sports',
    count: [Theme.Fomo, Theme.Luna, Theme.Turboslots].includes(theme)
        ? sportsFomo.length
        : sports.length
};
let realm = getRealm();
stateRealm.subscribe((value) => {
    if (realm === value)
        return;
    realm = value;
    initialized = false;
    _loadedGeoGroup = null;
    if (browser) {
        initializeGameCatalog(true, true);
    }
});
stateCountry.subscribe((value) => {
    if (_country === value || !value)
        return;
    _country = value;
    const newGeoGroup = getGeoGroupForCountry(value);
    if (_geoGroup !== newGeoGroup) {
        _geoGroup = newGeoGroup;
        _loadedGeoGroup = null;
        if (browser) {
            initializeGameCatalog(false, true);
        }
    }
});
